import { useState } from 'react';
import { createChargerOnBackend, createSimulatorOnDaemon, type SimulatorSnapshot } from '../lib/api';

interface Props {
  existing: SimulatorSnapshot[];
  onClose: () => void;
  onDone: () => Promise<void> | void;
}

interface Progress {
  charger_id: string;
  status: 'pending' | 'ok' | 'skipped' | 'failed';
  error?: string;
}

const PREFIX_DEFAULT = 'SIMV1';

/** Find the highest number suffix already used for chargers matching prefix + N-digit number. */
function nextStartNumber(existing: SimulatorSnapshot[], prefix: string): number {
  let max = 999;
  const re = new RegExp(`^${prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(\\d+)$`);
  for (const s of existing) {
    const m = s.charger_id.match(re);
    if (m) {
      const n = Number(m[1]);
      if (Number.isFinite(n) && n > max) max = n;
    }
  }
  return max + 1;
}

export default function BulkAddModal({ existing, onClose, onDone }: Props) {
  const [count, setCount] = useState(10);
  const [prefix, setPrefix] = useState(PREFIX_DEFAULT);
  const [startNum, setStartNum] = useState(() => nextStartNumber(existing, PREFIX_DEFAULT));
  const [pad, setPad] = useState(4);
  const [centerLat, setCenterLat] = useState('17.4400');
  const [centerLng, setCenterLng] = useState('78.4900');
  const [radiusKm, setRadiusKm] = useState('3');
  const [portCount, setPortCount] = useState(2);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<Progress[]>([]);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);

    // Validate
    const latN = Number(centerLat);
    const lngN = Number(centerLng);
    const radiusN = Number(radiusKm);
    if (!Number.isFinite(latN) || !Number.isFinite(lngN)) { setError('Center lat/lng must be numbers'); setBusy(false); return; }
    if (!Number.isFinite(radiusN) || radiusN <= 0) { setError('Radius must be a positive number'); setBusy(false); return; }
    if (count < 1 || count > 100) { setError('Count must be 1–100'); setBusy(false); return; }

    // ~111 km per degree lat. Convert radius km → degrees.
    const radiusDeg = radiusN / 111;

    // Pre-compute all charger definitions.
    const defs = Array.from({ length: count }, (_, i) => {
      const angle = (i / count) * 2 * Math.PI;
      const idNum = startNum + i;
      const charger_id = `${prefix}${String(idNum).padStart(pad, '0')}`;
      return {
        charger_id,
        name: `Simulator ${idNum}`,
        location: `Bulk-added #${idNum}`,
        lat: +(latN + Math.sin(angle) * radiusDeg).toFixed(6),
        lng: +(lngN + Math.cos(angle) * radiusDeg).toFixed(6),
        port_count: portCount,
      };
    });

    const prog: Progress[] = defs.map((d) => ({ charger_id: d.charger_id, status: 'pending' }));
    setProgress(prog);

    // Fire them sequentially — creating 100 chargers in parallel would hammer
    // the backend and give worse error messages.
    for (let i = 0; i < defs.length; i++) {
      const d = defs[i]!;
      try {
        const ports = Array.from({ length: d.port_count }, (_, i) => ({
          port_number: i + 1,
          connector_type: '20A Socket - Max 3.3kW',
        }));
        await createChargerOnBackend({
          charger_id: d.charger_id,
          name: d.name,
          lat: d.lat,
          lng: d.lng,
          location: d.location,
          power_kw: 3.3,
          is_public: true,
          ports,
        });
        await createSimulatorOnDaemon(d.charger_id, d.port_count);
        prog[i] = { ...prog[i]!, status: 'ok' };
      } catch (err: any) {
        const msg = err?.response?.data?.error ?? err?.message ?? 'failed';
        // 409 = already exists, treat as "skipped" not "failed" so reruns are safe.
        const status = err?.response?.status === 409 ? 'skipped' : 'failed';
        prog[i] = { ...prog[i]!, status, error: msg };
      }
      setProgress([...prog]);
    }

    await onDone();
    setBusy(false);
  }

  const okCount = progress.filter((p) => p.status === 'ok').length;
  const failedCount = progress.filter((p) => p.status === 'failed').length;
  const skippedCount = progress.filter((p) => p.status === 'skipped').length;
  const done = progress.length > 0 && progress.every((p) => p.status !== 'pending');

  return (
    <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <form className="modal" style={{ maxWidth: 520 }} onSubmit={submit}>
        <h2>Bulk add chargers</h2>

        {progress.length === 0 ? (
          <>
            <div className="row" style={{ gap: 12 }}>
              <div className="field" style={{ flex: 1 }}>
                <label>Count (1–100)</label>
                <input type="number" min={1} max={100} value={count} onChange={(e) => setCount(Number(e.target.value))} />
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label>Port count each</label>
                <select value={portCount} onChange={(e) => setPortCount(Number(e.target.value))}>
                  <option value={1}>1</option>
                  <option value={2}>2</option>
                  <option value={3}>3</option>
                  <option value={4}>4</option>
                </select>
              </div>
            </div>

            <div className="row" style={{ gap: 12 }}>
              <div className="field" style={{ flex: 1 }}>
                <label>ID prefix</label>
                <input value={prefix} onChange={(e) => setPrefix(e.target.value.toUpperCase())} />
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label>Start number</label>
                <input type="number" value={startNum} onChange={(e) => setStartNum(Number(e.target.value))} />
              </div>
              <div className="field" style={{ width: 90 }}>
                <label>Pad digits</label>
                <input type="number" min={1} max={8} value={pad} onChange={(e) => setPad(Number(e.target.value))} />
              </div>
            </div>

            <div className="muted" style={{ marginBottom: 8 }}>
              Preview: <code>{prefix}{String(startNum).padStart(pad, '0')}</code> …
              <code>{prefix}{String(startNum + count - 1).padStart(pad, '0')}</code>
            </div>

            <div className="row" style={{ gap: 12 }}>
              <div className="field" style={{ flex: 1 }}>
                <label>Center latitude</label>
                <input value={centerLat} onChange={(e) => setCenterLat(e.target.value)} />
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label>Center longitude</label>
                <input value={centerLng} onChange={(e) => setCenterLng(e.target.value)} />
              </div>
              <div className="field" style={{ width: 100 }}>
                <label>Radius (km)</label>
                <input value={radiusKm} onChange={(e) => setRadiusKm(e.target.value)} />
              </div>
            </div>

            <div className="muted">
              Chargers are placed evenly on a circle of the given radius around the center.
            </div>

            <div className="row mt" style={{ gap: 8 }}>
              <button type="submit" disabled={busy} style={{ flex: 1 }}>Create {count}</button>
              <button type="button" className="ghost" onClick={onClose} disabled={busy}>Cancel</button>
            </div>
          </>
        ) : (
          <>
            <div className="muted" style={{ marginBottom: 12 }}>
              {okCount} created · {skippedCount} skipped · {failedCount} failed · {progress.length - okCount - skippedCount - failedCount} pending
            </div>
            <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8, padding: 8 }}>
              {progress.map((p) => (
                <div key={p.charger_id} className="row between" style={{ padding: '4px 0', fontSize: 13 }}>
                  <span>{p.charger_id}</span>
                  <span className={`pill ${p.status === 'ok' ? 'online' : p.status === 'failed' ? 'offline' : p.status === 'skipped' ? 'outage' : ''}`} style={{ fontSize: 11 }}>
                    {p.status === 'pending' ? '…' : p.status}
                    {p.error && ` — ${p.error}`}
                  </span>
                </div>
              ))}
            </div>
            <div className="row mt" style={{ gap: 8 }}>
              <button type="button" onClick={onClose} disabled={!done} style={{ flex: 1 }}>
                {done ? 'Close' : 'Working…'}
              </button>
            </div>
          </>
        )}
        {error && <div className="error">{error}</div>}
      </form>
    </div>
  );
}
